import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError, extractFieldErrors } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  ClipboardList,
  CheckCircle2,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";

const CRONICAS = [
  "Hipertensión",
  "Diabetes",
  "Asma",
  "Falla Renal",
  "Falla Hepática",
  "Glaucoma",
  "EPOC",
  "Cardiopatía",
  "Tiroides",
  "Otra",
];

const GENERO_OPTIONS = [
  { value: "mujer", label: "Mujer" },
  { value: "hombre", label: "Hombre" },
  { value: "mujer_trans", label: "Mujer trans" },
  { value: "hombre_trans", label: "Hombre trans" },
  { value: "intersex", label: "Intersex" },
  { value: "nb", label: "No binario (NB)" },
  { value: "otro", label: "Otro" },
];

const LEGACY_SEXO_TO_GENERO = { masculino: "hombre", femenino: "mujer" };

const DEFAULT_STATE = {
  demograficos: { edad: "", peso: "", altura: "", genero: "", genero_otro_texto: "" },
  alergias: {
    farmacos: { ninguna: false, items: [] },
    alimentos: { ninguna: false, items: [] },
  },
  enfermedades_cronicas: { ninguna: false, seleccionadas: [], otra_texto: "" },
  medicacion_actual: { ninguna: false, items: [] },
  condiciones_especiales: {
    embarazo: false,
    lactancia: false,
    cirugia_reciente: false,
    detalle: "",
  },
  habitos: {
    tabaquismo: "no",
    alcohol: "no",
    drogas: { frecuencia: "ninguna", detalle: "" },
  },
};

const STEPS = [
  { id: "demograficos", label: "Demográficos" },
  { id: "alergias", label: "Alergias" },
  { id: "cronicas", label: "Enfermedades crónicas" },
  { id: "medicacion", label: "Medicación actual" },
  { id: "condiciones", label: "Condiciones especiales" },
  { id: "habitos", label: "Hábitos" },
];

export default function HistoriaClinica() {
  const navigate = useNavigate();
  const [state, setState] = useState(DEFAULT_STATE);
  const [stepIdx, setStepIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/historia-clinica");
        const d = data.payload.demograficos || {};
        // Legacy `sexo` may still be present in old saved docs; map to `genero`.
        const generoRaw = d.genero || LEGACY_SEXO_TO_GENERO[d.sexo] || "";
        const h = data.payload.habitos || {};
        // Legacy `drogas.consume` → `drogas.frecuencia`.
        const drogas = h.drogas || {};
        const frecuencia =
          drogas.frecuencia ||
          (drogas.consume ? "ocasional" : "ninguna");
        setState({
          demograficos: {
            edad: String(d.edad ?? ""),
            peso: String(d.peso ?? ""),
            altura: String(d.altura ?? ""),
            genero: generoRaw,
            genero_otro_texto: d.genero_otro_texto || "",
          },
          alergias: data.payload.alergias,
          enfermedades_cronicas: data.payload.enfermedades_cronicas,
          medicacion_actual: data.payload.medicacion_actual,
          condiciones_especiales: data.payload.condiciones_especiales,
          habitos: {
            tabaquismo: h.tabaquismo || "no",
            alcohol: h.alcohol || "no",
            drogas: {
              frecuencia,
              detalle: drogas.detalle || "",
            },
          },
        });
      } catch (e) {
        // 404 → no historia yet, keep defaults; anything else → surface.
        if (e?.response?.status !== 404) {
          toast.error(formatApiError(e));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const goto = (i) => setStepIdx(Math.max(0, Math.min(STEPS.length - 1, i)));

  const buildPayload = () => ({
    demograficos: {
      edad: Number(state.demograficos.edad),
      peso: Number(state.demograficos.peso),
      altura: Number(state.demograficos.altura),
      genero: state.demograficos.genero,
      genero_otro_texto:
        state.demograficos.genero === "otro"
          ? (state.demograficos.genero_otro_texto || "").trim()
          : "",
    },
    alergias: state.alergias,
    enfermedades_cronicas: state.enfermedades_cronicas,
    medicacion_actual: {
      ninguna: state.medicacion_actual.ninguna,
      items: state.medicacion_actual.items
        .filter((m) => (m.nombre || "").trim())
        .map((m) => ({ nombre: m.nombre.trim(), dosis: (m.dosis || "").trim() })),
    },
    condiciones_especiales: state.condiciones_especiales,
    habitos: {
      tabaquismo: state.habitos.tabaquismo,
      alcohol: state.habitos.alcohol,
      drogas: {
        frecuencia: state.habitos.drogas.frecuencia || "ninguna",
        detalle:
          state.habitos.drogas.frecuencia && state.habitos.drogas.frecuencia !== "ninguna"
            ? (state.habitos.drogas.detalle || "").trim()
            : "",
      },
    },
  });

  const canSubmit = useMemo(() => {
    const d = state.demograficos;
    if (!d.genero || !d.edad || !d.peso || !d.altura) return false;
    const okAF = state.alergias.farmacos.ninguna || state.alergias.farmacos.items.length > 0;
    const okAA =
      state.alergias.alimentos.ninguna || state.alergias.alimentos.items.length > 0;
    const c = state.enfermedades_cronicas;
    const okC = c.ninguna || c.seleccionadas.length > 0;
    const okM =
      state.medicacion_actual.ninguna ||
      state.medicacion_actual.items.some((m) => (m.nombre || "").trim());
    return okAF && okAA && okC && okM;
  }, [state]);

  // Client-side validation. Runs before we hit the API so users see actionable
  // Spanish errors immediately without a round-trip.
  const validateClient = () => {
    const errs = {};
    const d = state.demograficos;
    const edad = Number(d.edad);
    const peso = Number(d.peso);
    const altura = Number(d.altura);
    if (!d.edad || Number.isNaN(edad) || edad < 0 || edad > 120) {
      errs["demograficos.edad"] = "La edad debe estar entre 0 y 120 años.";
    }
    if (!d.peso || Number.isNaN(peso) || peso < 1 || peso > 500) {
      errs["demograficos.peso"] = "El peso debe estar entre 1 y 500 kg.";
    }
    if (!d.altura || Number.isNaN(altura) || altura < 30 || altura > 250) {
      errs["demograficos.altura"] =
        "La altura debe estar entre 30 y 250 cm. Si mides 1.70 m, escribe 170.";
    }
    if (!d.genero) {
      errs["demograficos.genero"] = "Selecciona tu género.";
    }
    if (d.genero === "otro" && !(d.genero_otro_texto || "").trim()) {
      errs["demograficos.genero_otro_texto"] =
        "Especifica tu género en el campo 'Otro'.";
    }
    return errs;
  };

  const submit = async () => {
    setError(null);
    const localErrs = validateClient();
    if (Object.keys(localErrs).length > 0) {
      setFieldErrors(localErrs);
      // Jump user to the demograficos step (all rangeable fields live there).
      if (
        localErrs["demograficos.edad"] ||
        localErrs["demograficos.peso"] ||
        localErrs["demograficos.altura"] ||
        localErrs["demograficos.genero"] ||
        localErrs["demograficos.genero_otro_texto"]
      ) {
        setStepIdx(0);
      }
      const summary = Object.values(localErrs).join(" · ");
      setError(summary);
      toast.error(summary);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const payload = buildPayload();
      const { data } = await api.put("/historia-clinica", payload);
      toast.success(
        data.complete
          ? "Historia clínica guardada — completa."
          : "Historia clínica guardada (incompleta)."
      );
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const { fieldErrors: fe, summary } = extractFieldErrors(e);
      const finalFE = Object.keys(fe).length ? fe : {};
      setFieldErrors(finalFE);
      const msg = summary || formatApiError(e);
      setError(msg);
      toast.error(msg);
      // If any demographic error, jump to that step.
      if (
        finalFE["demograficos.edad"] ||
        finalFE["demograficos.peso"] ||
        finalFE["demograficos.altura"] ||
        finalFE["demograficos.genero"] ||
        finalFE["demograficos.genero_otro_texto"]
      ) {
        setStepIdx(0);
      }
    } finally {
      setSaving(false);
    }
  };

  const stepId = STEPS[stepIdx].id;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
            <ClipboardList size={12} /> Historia clínica
          </div>
          <h1 className="mt-3 font-display text-3xl font-semibold text-[#0F2942] tracking-tight">
            Captura tu historia clínica
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg">
            Este perfil se cifra con AES-256-GCM antes de guardarse. Sirve para que la consulta con
            IA cruce automáticamente tus alergias, condiciones y medicación.
          </p>
        </div>

        <Stepper current={stepIdx} onSelect={goto} />

        <section
          className="rounded-xl border border-border bg-white p-6 sm:p-7 mt-6"
          data-testid={`historia-step-${stepId}`}
        >
          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Cargando…
            </div>
          ) : (
            <>
              {stepId === "demograficos" && (
                <Demograficos state={state} setState={setState} fieldErrors={fieldErrors} />
              )}
              {stepId === "alergias" && <Alergias state={state} setState={setState} />}
              {stepId === "cronicas" && <Cronicas state={state} setState={setState} />}
              {stepId === "medicacion" && <Medicacion state={state} setState={setState} />}
              {stepId === "condiciones" && (
                <Condiciones state={state} setState={setState} />
              )}
              {stepId === "habitos" && <Habitos state={state} setState={setState} fieldErrors={fieldErrors} />}
            </>
          )}
        </section>

        {error && (
          <div
            className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm px-3 py-2"
            data-testid="historia-error"
          >
            {error}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => (stepIdx === 0 ? navigate(-1) : goto(stepIdx - 1))}
            data-testid="historia-prev-btn"
          >
            <ArrowLeft size={14} /> {stepIdx === 0 ? "Cancelar" : "Anterior"}
          </Button>
          {stepIdx < STEPS.length - 1 ? (
            <Button
              onClick={() => {
                // Only guard leaving the Demograficos step (index 0) — that's where
                // the range-validated fields live. Other steps are booleans/lists.
                if (stepIdx === 0) {
                  const errs = validateClient();
                  if (Object.keys(errs).length > 0) {
                    setFieldErrors(errs);
                    const summary = Object.values(errs).join(" · ");
                    toast.error(summary);
                    return;
                  }
                  setFieldErrors({});
                }
                goto(stepIdx + 1);
              }}
              className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
              data-testid="historia-next-btn"
            >
              Siguiente <ArrowRight size={14} />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={!canSubmit || saving}
              className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
              data-testid="historia-submit-btn"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Guardando…
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} /> Guardar historia clínica
                </>
              )}
            </Button>
          )}
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

// -------- Stepper --------
function Stepper({ current, onSelect }) {
  return (
    <ol className="flex gap-2 overflow-x-auto" data-testid="historia-stepper">
      {STEPS.map((s, i) => (
        <li key={s.id} className="shrink-0">
          <button
            type="button"
            onClick={() => onSelect(i)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
              i === current
                ? "border-[#2563EB] bg-[#2563EB]/8 text-[#0F2942]"
                : "border-border bg-white text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`step-btn-${s.id}`}
          >
            <span
              className={`w-5 h-5 grid place-items-center rounded-full text-[10px] font-semibold ${
                i === current ? "bg-[#2563EB] text-white" : "bg-muted text-muted-foreground"
              }`}
            >
              {i + 1}
            </span>
            {s.label}
          </button>
        </li>
      ))}
    </ol>
  );
}

// -------- Step components --------

function Demograficos({ state, setState, fieldErrors = {} }) {
  const set = (k) => (v) =>
    setState((s) => ({ ...s, demograficos: { ...s.demograficos, [k]: v } }));
  const d = state.demograficos;
  const err = (k) => fieldErrors[`demograficos.${k}`];
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label>Edad (años)</Label>
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          max={120}
          value={d.edad}
          onChange={(e) => set("edad")(e.target.value)}
          data-testid="dem-edad"
          aria-invalid={!!err("edad")}
        />
        <p className="text-[11px] text-muted-foreground mt-1">Rango permitido: 0 – 120 años.</p>
        {err("edad") && (
          <p className="text-[11px] text-destructive mt-1" data-testid="err-dem-edad">
            {err("edad")}
          </p>
        )}
      </div>
      <div>
        <Label>Género</Label>
        <Select
          value={d.genero}
          onValueChange={(v) => set("genero")(v)}
        >
          <SelectTrigger data-testid="dem-genero-trigger" aria-invalid={!!err("genero")}>
            <SelectValue placeholder="Selecciona" />
          </SelectTrigger>
          <SelectContent>
            {GENERO_OPTIONS.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                data-testid={`dem-genero-${opt.value}`}
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {err("genero") && (
          <p className="text-[11px] text-destructive mt-1" data-testid="err-dem-genero">
            {err("genero")}
          </p>
        )}
        {d.genero === "otro" && (
          <div className="mt-2">
            <Label className="text-xs">Especifica tu género</Label>
            <Input
              value={d.genero_otro_texto || ""}
              onChange={(e) => set("genero_otro_texto")(e.target.value)}
              placeholder="Descríbelo con tus palabras"
              data-testid="dem-genero-otro-texto"
              maxLength={80}
              aria-invalid={!!err("genero_otro_texto")}
            />
            {err("genero_otro_texto") && (
              <p className="text-[11px] text-destructive mt-1" data-testid="err-dem-genero-otro">
                {err("genero_otro_texto")}
              </p>
            )}
          </div>
        )}
      </div>
      <div>
        <Label>Peso (kg)</Label>
        <Input
          type="number"
          inputMode="decimal"
          min={1}
          max={500}
          step="0.1"
          placeholder="Ej. 70"
          value={d.peso}
          onChange={(e) => set("peso")(e.target.value)}
          data-testid="dem-peso"
          aria-invalid={!!err("peso")}
        />
        <p className="text-[11px] text-muted-foreground mt-1">Rango permitido: 1 – 500 kg.</p>
        {err("peso") && (
          <p className="text-[11px] text-destructive mt-1" data-testid="err-dem-peso">
            {err("peso")}
          </p>
        )}
      </div>
      <div>
        <Label>Altura (cm)</Label>
        <Input
          type="number"
          inputMode="numeric"
          min={30}
          max={250}
          step="1"
          placeholder="Ej. 170"
          value={d.altura}
          onChange={(e) => set("altura")(e.target.value)}
          data-testid="dem-altura"
          aria-invalid={!!err("altura")}
        />
        <p className="text-[11px] text-muted-foreground mt-1">
          Rango: 30 – 250 cm. Si mides 1.70 m, escribe <b>170</b>.
        </p>
        {err("altura") && (
          <p className="text-[11px] text-destructive mt-1" data-testid="err-dem-altura">
            {err("altura")}
          </p>
        )}
      </div>
    </div>
  );
}

function TagList({ items, onAdd, onRemove, testidPrefix, placeholder }) {
  const [draft, setDraft] = useState("");
  return (
    <div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testidPrefix}-input`}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (draft.trim()) {
                onAdd(draft.trim());
                setDraft("");
              }
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (draft.trim()) {
              onAdd(draft.trim());
              setDraft("");
            }
          }}
          data-testid={`${testidPrefix}-add-btn`}
        >
          <Plus size={14} /> Agregar
        </Button>
      </div>
      {items.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs"
              data-testid={`${testidPrefix}-item-${i}`}
            >
              {it}
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="text-muted-foreground hover:text-red-600"
                aria-label={`Eliminar ${it}`}
              >
                <Trash2 size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Alergias({ state, setState }) {
  const upd = (key) => (patch) =>
    setState((s) => ({
      ...s,
      alergias: { ...s.alergias, [key]: { ...s.alergias[key], ...patch } },
    }));
  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Declara alergias explícitamente. Si no tienes, marca &quot;Ninguna&quot; en cada categoría.
      </p>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Alergias a fármacos</Label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={state.alergias.farmacos.ninguna}
              onCheckedChange={(v) => upd("farmacos")({ ninguna: !!v, items: v ? [] : state.alergias.farmacos.items })}
              data-testid="alergias-farmacos-ninguna"
            />
            Ninguna
          </label>
        </div>
        {!state.alergias.farmacos.ninguna && (
          <TagList
            items={state.alergias.farmacos.items}
            onAdd={(v) => upd("farmacos")({ items: [...state.alergias.farmacos.items, v] })}
            onRemove={(i) =>
              upd("farmacos")({
                items: state.alergias.farmacos.items.filter((_, j) => j !== i),
              })
            }
            testidPrefix="alergias-farmacos"
            placeholder="Ej. penicilina, ibuprofeno"
          />
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Alergias a alimentos</Label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={state.alergias.alimentos.ninguna}
              onCheckedChange={(v) => upd("alimentos")({ ninguna: !!v, items: v ? [] : state.alergias.alimentos.items })}
              data-testid="alergias-alimentos-ninguna"
            />
            Ninguna
          </label>
        </div>
        {!state.alergias.alimentos.ninguna && (
          <TagList
            items={state.alergias.alimentos.items}
            onAdd={(v) => upd("alimentos")({ items: [...state.alergias.alimentos.items, v] })}
            onRemove={(i) =>
              upd("alimentos")({
                items: state.alergias.alimentos.items.filter((_, j) => j !== i),
              })
            }
            testidPrefix="alergias-alimentos"
            placeholder="Ej. maní, mariscos"
          />
        )}
      </div>
    </div>
  );
}

function Cronicas({ state, setState }) {
  const c = state.enfermedades_cronicas;
  const set = (patch) =>
    setState((s) => ({ ...s, enfermedades_cronicas: { ...s.enfermedades_cronicas, ...patch } }));
  const toggle = (val) =>
    set({
      seleccionadas: c.seleccionadas.includes(val)
        ? c.seleccionadas.filter((x) => x !== val)
        : [...c.seleccionadas, val],
      ninguna: false,
    });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Enfermedades crónicas</Label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={c.ninguna}
            onCheckedChange={(v) =>
              set({ ninguna: !!v, seleccionadas: v ? [] : c.seleccionadas, otra_texto: v ? "" : c.otra_texto })
            }
            data-testid="cronicas-ninguna"
          />
          Ninguna
        </label>
      </div>
      {!c.ninguna && (
        <>
          <div className="grid sm:grid-cols-2 gap-2">
            {CRONICAS.map((name) => (
              <label
                key={name}
                className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={c.seleccionadas.includes(name)}
                  onCheckedChange={() => toggle(name)}
                  data-testid={`cronicas-${name.toLowerCase().replace(/\s+/g, "-").replace(/á/g, "a").replace(/é/g, "e").replace(/í/g, "i").replace(/ó/g, "o").replace(/ú/g, "u")}`}
                />
                {name}
              </label>
            ))}
          </div>
          {c.seleccionadas.includes("Otra") && (
            <div>
              <Label className="text-xs">Detalle de &quot;Otra&quot;</Label>
              <Input
                value={c.otra_texto}
                onChange={(e) => set({ otra_texto: e.target.value })}
                placeholder="Especifica la enfermedad"
                data-testid="cronicas-otra-texto"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Medicacion({ state, setState }) {
  const m = state.medicacion_actual;
  const set = (patch) =>
    setState((s) => ({ ...s, medicacion_actual: { ...s.medicacion_actual, ...patch } }));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Medicación actual</Label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={m.ninguna}
            onCheckedChange={(v) => set({ ninguna: !!v, items: v ? [] : m.items })}
            data-testid="med-ninguna"
          />
          Ninguna
        </label>
      </div>
      {!m.ninguna && (
        <>
          <div className="space-y-2">
            {m.items.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                <Input
                  value={row.nombre}
                  onChange={(e) => {
                    const next = [...m.items];
                    next[i] = { ...next[i], nombre: e.target.value };
                    set({ items: next });
                  }}
                  placeholder="Medicamento"
                  data-testid={`med-nombre-${i}`}
                />
                <Input
                  value={row.dosis}
                  onChange={(e) => {
                    const next = [...m.items];
                    next[i] = { ...next[i], dosis: e.target.value };
                    set({ items: next });
                  }}
                  placeholder="Dosis (ej. 500 mg c/8h)"
                  data-testid={`med-dosis-${i}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => set({ items: m.items.filter((_, j) => j !== i) })}
                  data-testid={`med-remove-${i}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => set({ items: [...m.items, { nombre: "", dosis: "" }] })}
            data-testid="med-add-btn"
          >
            <Plus size={14} /> Agregar medicamento
          </Button>
        </>
      )}
    </div>
  );
}

function Condiciones({ state, setState }) {
  const c = state.condiciones_especiales;
  const set = (patch) =>
    setState((s) => ({
      ...s,
      condiciones_especiales: { ...s.condiciones_especiales, ...patch },
    }));
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 rounded-md border border-border bg-white px-3 py-2 text-sm">
        <Checkbox
          checked={c.embarazo}
          onCheckedChange={(v) => set({ embarazo: !!v })}
          data-testid="cond-embarazo"
        />
        Embarazo
      </label>
      <label className="flex items-center gap-3 rounded-md border border-border bg-white px-3 py-2 text-sm">
        <Checkbox
          checked={c.lactancia}
          onCheckedChange={(v) => set({ lactancia: !!v })}
          data-testid="cond-lactancia"
        />
        Lactancia
      </label>
      <label className="flex items-center gap-3 rounded-md border border-border bg-white px-3 py-2 text-sm">
        <Checkbox
          checked={c.cirugia_reciente}
          onCheckedChange={(v) => set({ cirugia_reciente: !!v })}
          data-testid="cond-cirugia"
        />
        Cirugía reciente (últimos 3 meses)
      </label>
      <div>
        <Label>Detalles (opcional)</Label>
        <Input
          value={c.detalle}
          onChange={(e) => set({ detalle: e.target.value })}
          data-testid="cond-detalle"
        />
      </div>
    </div>
  );
}

function Habitos({ state, setState, fieldErrors = {} }) {
  const h = state.habitos;
  const set = (patch) => setState((s) => ({ ...s, habitos: { ...s.habitos, ...patch } }));
  const drogasErr = fieldErrors["habitos.drogas.frecuencia"];
  return (
    <div className="space-y-6">
      <div>
        <Label className="mb-2 block">Tabaquismo</Label>
        <RadioGroup
          value={h.tabaquismo}
          onValueChange={(v) => set({ tabaquismo: v })}
          className="grid grid-cols-3 gap-2"
        >
          {["no", "ocasional", "diario"].map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm capitalize cursor-pointer"
            >
              <RadioGroupItem value={opt} data-testid={`hab-tab-${opt}`} />
              {opt}
            </label>
          ))}
        </RadioGroup>
      </div>
      <div>
        <Label className="mb-2 block">Alcohol</Label>
        <RadioGroup
          value={h.alcohol}
          onValueChange={(v) => set({ alcohol: v })}
          className="grid grid-cols-3 gap-2"
        >
          {["no", "ocasional", "frecuente"].map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm capitalize cursor-pointer"
            >
              <RadioGroupItem value={opt} data-testid={`hab-alc-${opt}`} />
              {opt}
            </label>
          ))}
        </RadioGroup>
      </div>
      <div>
        <Label className="mb-2 block">Drogas</Label>
        <RadioGroup
          value={h.drogas.frecuencia || "ninguna"}
          onValueChange={(v) =>
            set({
              drogas: {
                frecuencia: v,
                detalle: v === "ninguna" ? "" : h.drogas.detalle || "",
              },
            })
          }
          className="grid grid-cols-3 gap-2"
        >
          {[
            { value: "ninguna", label: "Ninguna" },
            { value: "ocasional", label: "Ocasional" },
            { value: "frecuente", label: "Frecuente" },
          ].map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm cursor-pointer"
            >
              <RadioGroupItem value={opt.value} data-testid={`hab-drogas-${opt.value}`} />
              {opt.label}
            </label>
          ))}
        </RadioGroup>
        {drogasErr && (
          <p className="text-[11px] text-destructive mt-1" data-testid="err-hab-drogas">
            {drogasErr}
          </p>
        )}
        {h.drogas.frecuencia && h.drogas.frecuencia !== "ninguna" && (
          <div className="mt-2">
            <Label className="text-xs">Sustancia (opcional)</Label>
            <Input
              value={h.drogas.detalle || ""}
              onChange={(e) =>
                set({ drogas: { ...h.drogas, detalle: e.target.value } })
              }
              placeholder="Ej. cannabis, cocaína, etc."
              data-testid="hab-drogas-detalle"
              maxLength={200}
            />
          </div>
        )}
      </div>
    </div>
  );
}
